import React from "react";
import { defaultIConfig, IChat, IConfig, IProject, IUser } from "@/lib/types";
import type { LoadingState, StartStreamOptions } from "@/lib/streamSession";
import type { StreamManager } from "@/hooks/useStreamManager";

export interface IUserContext {
    user: IUser | undefined;
    updateUser: (user: IUser | undefined) => void;
}

export const UserContext = React.createContext<IUserContext>({
    user: undefined,
    updateUser: () => {
        return;
    },
});

export interface IApiError {
    message: string;
    type: "cors" | "network" | "auth" | "api" | "unknown" | "warning";
    details?: string;
    timestamp: number;
}

export interface IErrorContext {
    errors: IApiError[];
    addError: (error: IApiError) => void;
    clearError: (timestamp: number) => void;
    clearAllErrors: () => void;
}

export const ErrorContext = React.createContext<IErrorContext>({
    errors: [],
    addError: () => {
        return;
    },
    clearError: () => {
        return;
    },
    clearAllErrors: () => {
        return;
    },
});

export interface IChatsContext {
    chats: IChat[];
    setChats: React.Dispatch<React.SetStateAction<IChat[]>>;
    updateChat: (chat: Partial<IChat>, doNetwork: boolean, doUpdateTime?: boolean) => Promise<void>;
    deleteChat: (id: string) => Promise<void>;
}

export const ChatsContext = React.createContext<IChatsContext>({
    chats: [],
    setChats: () => {
        return;
    },
    updateChat: async () => {
        return;
    },
    deleteChat: async () => {
        return;
    },
});

export interface IProjectsContext {
    projects: IProject[];
    setProjects: (projects: IProject[]) => void;
    createProject: (project: Pick<IProject, "title" | "description">) => Promise<IProject>;
    updateProject: (project: Partial<IProject>) => Promise<void>;
    deleteProject: (id: string) => Promise<void>;
}

export const ProjectsContext = React.createContext<IProjectsContext>({
    projects: [],
    setProjects: () => {
        return;
    },
    createProject: async () => ({}) as IProject,
    updateProject: async () => {
        return;
    },
    deleteProject: async () => {
        return;
    },
});

export interface IStreamingContext {
    startStream: (chat: IChat, options?: StartStreamOptions) => void;
    stopStream: (chatid: string, commitPartial?: boolean) => void;
    // Set a chat's loading state directly (e.g. the page's "loading messages"
    // spinner, which is independent of an active stream).
    setLoadingState: (chatid: string, updater: LoadingState | ((prev: LoadingState) => LoadingState)) => void;
    // The chat the user is currently viewing. Used to gate canvas auto-open so a
    // background chat finishing a document doesn't yank the canvas open over the
    // chat in view.
    setViewedChatId: (chatid: string | null) => void;
    // Manager handle for the subscription hooks (useChatStream/useIsStreaming).
    // Not consumed directly by components — use the hooks.
    _manager: StreamManager;
}

// Inert session used by the default (provider-less) context so hooks like
// useIsStreaming/useChatStream don't throw when rendered outside the provider
// (e.g. isolated component tests). Reports nothing streaming and never emits.
const noopSnapshot = {
    streamingBlocks: [],
    committedMessages: [],
    loadingState: { loading: false, thinking: false, thinkingLabel: "" },
    isStreaming: false,
    streamReceiving: false,
    tokenUsage: null,
};
const noopSession = {
    chatid: "",
    getSnapshot: () => noopSnapshot,
    subscribe: () => () => {},
    start: () => {},
    stop: () => {},
    setLoadingState: () => {},
    clearTokenUsage: () => {},
    isStreaming: () => false,
    hasListeners: () => false,
    destroy: () => {},
};
const noopManager: StreamManager = {
    startStream: () => {},
    stopStream: () => {},
    getSession: () => noopSession,
    isStreaming: () => false,
    setLoadingState: () => {},
    clearTokenUsage: () => {},
    destroyAll: () => {},
};

export const StreamingContext = React.createContext<IStreamingContext>({
    startStream: () => {
        return;
    },
    stopStream: () => {
        return;
    },
    setLoadingState: () => {
        return;
    },
    setViewedChatId: () => {
        return;
    },
    _manager: noopManager,
});

export interface IConfigContext {
    config: IConfig;
    updateConfig: (config: IConfig) => void;
}

export const ConfigContext = React.createContext<IConfigContext>({
    config: defaultIConfig,
    updateConfig: () => {
        return;
    },
});
